<template>
  <div
    class="
      main-product
      component-section
      critical-component-clear
    "
  >
    <div class="main-product__grid">
      <div class="main-product__header">
        <h1
          :id="`section-title-${liquid.sectionId}`"
          class="main-product__title text-body-xs-label text-body-s-label-desktop"
          v-text="formattedTitle"
        />

        <p
          v-if="subtitle"
          class="main-product__subtitle text-heading-l text-heading-2xl-desktop"
          v-text="subtitle"
        />

        <div
          class="
            main-product__prices-container
            text-body-xs-label
            text-body-s-label-desktop
          "
        >
          <product-prices
            :available="product?.available"
            block-class="main-product"
            :compare-at-price="product?.compareAtPrice?.amount || liquid.compareAtPrice"
            :is-gift-card="product?.isGiftCard || liquid.isGiftCard"
            :price="product?.price?.amount || liquid.price"
            element="span"
          />
        </div>
      </div>

      <div class="main-product__gallery">
        <product-gallery :items="product.images" />
      </div>

      <div class="main-product__aside">
        <div class="main-product__form">
          <product-form
            :group="group"
            :is-gift-card="product?.isGiftCard || liquid.isGiftCard"
            :liquid="liquid"
            :parent-state="state"
            :product="product"
            :variant="variant"
            :size-guide="sizeGuide"
            :size-title="sizeTitle"
            :size-body="sizeBody"
            :size-page-cm="sizePageCm"
            :size-page-in="sizePageIn"
            :size-guide-left-image="leftImage"
            :size-guide-right-image="rightImage"
            :enable-preorder="enablePreorder"
            :klaviyo-form-id="klaviyoFormId"
            :klaviyo-form-id-mobile="klaviyoFormIdMobile"
            @update-variant="handleVariantUpdate"
          />
        </div>

        <ul
          v-if="bullets"
          class="main-product__bullets text-body-s"
        >
          <li
            v-for="(bullet) of bullets"
            :key="bullet"
            v-text="bullet"
          />
        </ul>

        <div
          class="main-product__description text-body-s rte"
          v-html="product.description || liquid.description"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {String} handle - Product page's original handle.
 * @param {String} liquid.description - Product's Liquid description.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {String} liquid.price - Product's Liquid price.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Product's Liquid title.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {String} sizeGuide - The size guide link.
 * @param {String} sizeTitle - The size guide title.
 * @param {String} sizeBody - The size guide body.
 * @param {String} sizePageCm - The size guide page content.
 * @param {String} sizePageIn - The size guide page content.
 * @param {Boolean} enablePreorder - Whether to enable pre-order.
 * @param {String} klaviyoFormId - The Klaviyo form ID.
 * @param {Object} rightImage - The right image object of size guide.
 * @param {Object} leftImage - The left image object of size guide.
 */
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductForm from '~async/product-form/product-form'
import ProductPrices from '~global/product-prices/product-prices'

import product from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'
import { emitUpdateRequestObject } from '~/helpers/vue'

export default {
  name: 'MainProduct',

  components: {
    ProductForm,
    ProductPrices,
    ProductGallery,
  },

  props: {
    handle: {
      type: [Boolean, String],
      default: cnvs.page.handle,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        hasOnlyDefaultVariant: false,
        price: 0,
        sectionId: '',
        title: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    group: {
      type: [Boolean, String],
      default: '',
    },
    sizeGuide: {
      type: String,
      default: '',
    },
    sizeTitle: {
      type: String,
      default: '',
    },
    sizeBody: {
      type: String,
      default: '',
    },
    sizePageCm: {
      type: String,
      default: '',
    },
    sizePageIn: {
      type: String,
      default: '',
    },
    rightImage: {
      type: String,
      default: '',
    },
    leftImage: {
      type: String,
      default: '',
    },
    enablePreorder: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      currentHandle: cnvs.page.handle,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Returns the formatted product title.
     * @returns {String}
     */
    formattedTitle() {
      const title = this.product.title || this.liquid.title
      let split = ':'

      if (!title.includes(':') && title.includes('.')) {
        split = '.'
      }

      return title.split(split)[0]
    },

    /**
     * Returns the product subtitle.
     * @returns {String|false}
     */
    subtitle() {
      if (this.product?.subtitle) {
        return this.product.subtitle.value
      }

      return this.liquid.subtitle ?? false
    },

    /**
     * Returns the product bullets.
     * @returns {Array|false}
     */
    bullets() {
      if (!this.product?.bullets) {
        return false
      }

      return this.product.bullets.value.split('#').slice(1)
    },
  },

  watch: {

    /**
     * Watch currentHandle for update to load new product.
     */
    currentHandle() {
      this.queryProduct(this.currentHandle)
    },

    variant(newValue) {
      if (newValue) {
        const products = [
          {
            id: this.variant.sku,
            name: this.product.title,
            brand: this.product.vendor,
            category: this.product.type,
            variant: this.variant.title,
            price: (this.variant.price.amount / 100).toFixed(2),
            product_id: this.product.id,
            variant_id: this.variant.id,
            image: this.product.featuredImage.src,
          },
        ]

        window.ElevarDataLayer.push({
          event: 'dl_view_item',
          user_properties: [],
          ecommerce: {
            currencyCode: window.cnvs.store.currency,
            detail: {
              actionField: {
                list: document.referrer,
                action: 'detail',
              },
              products,
            },
          },
        })
      }
    },
  },

  mounted() {
    this.queryProduct()
  },

  methods: {

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false) {
        return
      }

      if (this.product.handle === handle) {
        this.state = 'ready'
        return
      }

      try {
        const variables = {
          country: cnvs.store.country,
          handle,
          language: cnvs.store.language,
        }

        if (this.group) {
          variables.siblingQuery = `tag:link: ${this.group}`
        }

        const response = await cnvs.Query({
          query: product,
          variables,
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatProduct(response, this.liquid),
          type: 'product',
        })

        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const url = variant?.id
        ? `${location.origin}${location.pathname}?variant=${variant.id}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },
  },
}
</script>

<style lang="scss">
@import './main-product';
</style>
